import React, { useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";

/*
A calm, operator-friendly "route sheet": stops grouped into compact, non-
overlapping territories, each served by one vehicle in drive order, starting
from a marked base/provider/hub. Reveals gently on scroll (respects reduced
motion). Purely illustrative — deterministic per `seed`.

Territories come from clustering, not round-robin, so routes don't criss-cross:
  - vehicles === depots  -> each stop joins its nearest depot (Voronoi)
  - vehicles  >  depots  -> seeded k-means into `routes` clusters, each starting
                            from the depot nearest its centroid
*/
const FONT = 'bold 11px "Open Sans", -apple-system, "Segoe UI", Roboto, sans-serif';
const CORAL = "#d9662f";
const CARD = "#ffffff";
// Muted, operator-friendly hues so each vehicle's territory reads apart. Index
// 0 is replaced with the theme's teal at render time.
const EXTRA_COLORS = ["#3f6fb0", "#6fa06a", "#c39a35", "#8a6fb0", "#4a9a94", "#b06f8f"];

function rng(seed) {
  let s = seed || 1;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function nearestIndex(p, list) {
  let bi = 0;
  let bd = Infinity;
  for (let i = 0; i < list.length; i++) {
    const dx = p.x - list[i].x;
    const dy = p.y - list[i].y;
    const dd = dx * dx + dy * dy;
    if (dd < bd) { bd = dd; bi = i; }
  }
  return bi;
}

// Seeded k-means with farthest-first initialization → compact, separated groups.
function kmeans(pts, k, r) {
  const centers = [{ x: pts[Math.floor(r() * pts.length)].x, y: pts[Math.floor(r() * pts.length)].y }];
  while (centers.length < k) {
    let best = 0;
    let bestD = -1;
    for (let i = 0; i < pts.length; i++) {
      let d = Infinity;
      for (let c = 0; c < centers.length; c++) {
        const dx = pts[i].x - centers[c].x;
        const dy = pts[i].y - centers[c].y;
        d = Math.min(d, dx * dx + dy * dy);
      }
      if (d > bestD) { bestD = d; best = i; }
    }
    centers.push({ x: pts[best].x, y: pts[best].y });
  }
  const assign = new Array(pts.length).fill(0);
  for (let it = 0; it < 8; it++) {
    for (let i = 0; i < pts.length; i++) assign[i] = nearestIndex(pts[i], centers);
    const sx = new Array(k).fill(0);
    const sy = new Array(k).fill(0);
    const cnt = new Array(k).fill(0);
    for (let i = 0; i < pts.length; i++) { sx[assign[i]] += pts[i].x; sy[assign[i]] += pts[i].y; cnt[assign[i]]++; }
    for (let c = 0; c < k; c++) if (cnt[c]) centers[c] = { x: sx[c] / cnt[c], y: sy[c] / cnt[c] };
  }
  return assign;
}

export default function RouteSheetCanvas({
  seed = 1,
  stops = 12,
  depots = 1,
  routes = 1,
  depotLabel = "Base",
  aspect = "4 / 3.2",
  label = "A day's route",
}) {
  const theme = useTheme();
  const canvasRef = useRef(null);
  const stateRef = useRef({ ready: false });

  const teal = theme.palette.primary.main;
  const line = theme.palette.divider;
  const slate = theme.palette.text.secondary;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const routeColors = [teal, ...EXTRA_COLORS];

    function placeDepots(n, w, h, r) {
      const cols = n <= 3 ? n : Math.ceil(Math.sqrt(n));
      const rows = Math.ceil(n / cols);
      const padX = w * 0.16;
      const padY = h * 0.22;
      const list = [];
      for (let i = 0; i < n; i++) {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const cx = cols === 1 ? 0.5 : col / (cols - 1);
        const cy = rows === 1 ? 0.5 : row / (rows - 1);
        list.push({
          x: padX + cx * (w - 2 * padX) + (r() - 0.5) * w * 0.09,
          y: padY + cy * (h - 2 * padY) + (r() - 0.5) * h * 0.09,
        });
      }
      return list;
    }

    function tour(start, group) {
      const path = [start];
      const remaining = group.slice();
      let cur = start;
      while (remaining.length) {
        const bi = nearestIndex(cur, remaining);
        cur = remaining.splice(bi, 1)[0];
        path.push(cur);
      }
      path.push(start);
      return path;
    }

    function layout(w, h) {
      const r = rng(seed);
      const depotList = placeDepots(depots, w, h, r);
      const padX = w * 0.11;
      const padY = h * 0.13;
      const pts = [];
      for (let i = 0; i < stops; i++) {
        pts.push({ x: padX + r() * (w - 2 * padX), y: padY + r() * (h - 2 * padY) });
      }
      const assign =
        routes === depots
          ? pts.map((p) => nearestIndex(p, depotList))
          : kmeans(pts, routes, r);

      const paths = [];
      for (let g = 0; g < routes; g++) {
        const group = pts.filter((_, i) => assign[i] === g);
        if (!group.length) continue;
        let start;
        if (routes === depots) {
          start = depotList[g];
        } else {
          const cx = group.reduce((a, p) => a + p.x, 0) / group.length;
          const cy = group.reduce((a, p) => a + p.y, 0) / group.length;
          start = depotList[nearestIndex({ x: cx, y: cy }, depotList)];
        }
        paths.push({ pts: tour(start, group), color: g });
      }
      stateRef.current = { ready: true, depots: depotList, routes: paths };
    }

    function render(progress) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      if (!w) return;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      if (!stateRef.current.ready) layout(w, h);
      const { depots: depotList, routes: paths } = stateRef.current;

      // faint paper grid
      ctx.strokeStyle = line;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      for (let gx = w / 8; gx < w; gx += w / 8) { ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, h); ctx.stroke(); }
      for (let gy = w / 8; gy < h; gy += w / 8) { ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(w, gy); ctx.stroke(); }
      ctx.globalAlpha = 1;

      let totalStops = 0;
      paths.forEach((rt) => { totalStops += rt.pts.length - 2; });
      const shown = Math.round(progress * totalStops);

      // route lines up to the revealed stop, one color per vehicle
      let seen = 0;
      paths.forEach((rt) => {
        const path = rt.pts;
        const interior = path.length - 2;
        const upto = Math.max(0, Math.min(interior, shown - seen));
        ctx.strokeStyle = routeColors[rt.color % routeColors.length];
        ctx.lineWidth = 2.1;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.globalAlpha = 0.75;
        ctx.beginPath();
        ctx.moveTo(path[0].x, path[0].y);
        for (let j = 1; j <= upto; j++) ctx.lineTo(path[j].x, path[j].y);
        if (upto === interior && progress >= 1) ctx.lineTo(path[path.length - 1].x, path[path.length - 1].y);
        ctx.stroke();
        ctx.globalAlpha = 1;
        seen += interior;
      });

      // numbered stops in drive order, filled in their vehicle's color
      seen = 0;
      ctx.font = FONT;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      paths.forEach((rt) => {
        const path = rt.pts;
        const interior = path.length - 2;
        const col = routeColors[rt.color % routeColors.length];
        for (let j = 1; j <= interior; j++) {
          if (seen + j > shown) break;
          const p = path[j];
          ctx.beginPath();
          ctx.arc(p.x, p.y, 8.5, 0, 7);
          ctx.fillStyle = col;
          ctx.fill();
          ctx.lineWidth = 2;
          ctx.strokeStyle = CARD;
          ctx.stroke();
          ctx.fillStyle = "#fff";
          ctx.fillText(String(j), p.x, p.y + 0.5);
        }
        seen += interior;
      });

      // Base / Provider / Hub markers
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      depotList.forEach((dp) => {
        ctx.save();
        ctx.translate(dp.x, dp.y);
        ctx.fillStyle = CORAL;
        ctx.strokeStyle = CARD;
        ctx.lineWidth = 2.5;
        const s = 8;
        const rr = 3;
        ctx.beginPath();
        ctx.moveTo(-s + rr, -s);
        ctx.arcTo(s, -s, s, s, rr);
        ctx.arcTo(s, s, -s, s, rr);
        ctx.arcTo(-s, s, -s, -s, rr);
        ctx.arcTo(-s, -s, s, -s, rr);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = "#fff";
        ctx.font = 'bold 9px "Open Sans", sans-serif';
        ctx.fillText("★", 0, 0.5);
        ctx.restore();
        ctx.fillStyle = slate;
        ctx.font = '600 10px "Open Sans", sans-serif';
        ctx.fillText(depotLabel, dp.x, dp.y + 20);
        ctx.font = FONT;
      });
    }

    let raf;
    function animateIn() {
      if (reduce) { render(1); return; }
      let startTs = null;
      const dur = 1300;
      const step = (ts) => {
        if (startTs === null) startTs = ts;
        const p = Math.min(1, (ts - startTs) / dur);
        render(1 - Math.pow(1 - p, 2));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }

    let played = false;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !played) { played = true; animateIn(); }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(canvas);
    render(0);

    let rt;
    const onResize = () => {
      clearTimeout(rt);
      rt = setTimeout(() => { stateRef.current.ready = false; render(1); }, 150);
    };
    window.addEventListener("resize", onResize);

    return () => {
      io.disconnect();
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
      clearTimeout(rt);
    };
  }, [seed, stops, depots, routes, depotLabel, teal, line, slate]);

  return (
    <canvas
      ref={canvasRef}
      aria-label={label}
      role="img"
      style={{
        width: "100%",
        height: "auto",
        aspectRatio: aspect,
        display: "block",
        borderRadius: 16,
        background: "#fff",
        border: "1.5px solid",
        borderColor: line,
        boxShadow: "0 16px 38px -26px rgba(43,55,66,.30)",
      }}
    />
  );
}
